import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ROLES, type Role } from "@titleverify/shared";
import { apiErrorText, apiFetch } from "../api.js";
import { useAuth } from "../auth.js";
import { useMe } from "../me.js";
import { AdminBreadcrumb } from "./AdminHome.js";

/**
 * THE DOOR TO `POST /api/admin/users/:id/role`.
 *
 * This endpoint was built, tested and policy-gated, and had no UI. That is not a
 * cosmetic gap. Migration 0016 §4 states the design outright — "THE RUNTIME ROLE
 * CAN NEVER AGAIN CREATE THE FOUNDING ADMIN … Every admin after that is promoted
 * by tv_grant_role()" — so this endpoint IS the documented mechanism for every
 * admin after the first. Without a screen, adding a second admin needs
 * `titleverify_migrator` credentials and hand-written SQL
 * (scripts/seed-staging.ts), i.e. a non-technical operator cannot onboard a
 * colleague without a DBA. CLAUDE.md §2A forbids exactly that, with the same
 * binding force as the five non-negotiables.
 *
 * WHY THERE IS NO USER LIST HERE, AND WHY THAT IS NOT A SHORTCUT TAKEN.
 * The API offers no user listing and no user search — there is no
 * `GET /admin/users` of any shape (accounts/routes.ts exposes exactly two
 * routes: provisioning, and this grant). So a roster to browse is not something
 * this screen declined to build; it is something the server cannot answer. That
 * happens to be the right shape anyway: promoting someone is a deliberate act on
 * an identifier the operator was given out of band, not shopping through a
 * directory of staff. COMPLIANCE Constraint 1 governs professionals rather than
 * staff, but the same restraint is the correct default for a screen whose only
 * verb is "give this person power".
 *
 * The identifier is honest to obtain: App.tsx renders "Signed in as {userId}" on
 * every signed-in person's own account page, whatever their role. The colleague
 * reads their own account id there and sends it over. Nothing on this screen can
 * look it up for them, and this page says so rather than implying a search box
 * is coming.
 *
 * WHAT THE UI IS AND IS NOT RESPONSIBLE FOR HERE.
 *  - PROVENANCE IS ALREADY THE SERVER'S. `grantRole` (accounts/service.ts)
 *    writes an audit entry with `actorUserId`, `actorRole`,
 *    `action: "user.role_granted"`, the target as `entityId`, and
 *    `metadata: { from, to }` — who, when, and both ends of the move. This
 *    screen does not re-capture any of that and must not invent a second record
 *    of it. §2A is satisfied by the server, and the page says so plainly instead
 *    of collecting a field for show.
 *  - THERE IS NO SOURCE FIELD, AND ONE IS NOT FAKED. The route's zod schema is
 *    `z.object({ role })` and nothing else — a "reason" or "authorised by" typed
 *    here would be dropped on the floor by the server, so a box for it would be
 *    a lie about what is recorded. This is a real difference from professional
 *    verification, which does demand a written `verificationSource`, and the
 *    difference is stated to the operator rather than papered over.
 *  - NO RULE IS RE-IMPLEMENTED. Admin-only, no self-grant, no erased account, no
 *    no-op re-grant: all four are enforced by `authorize()`, by `grantRole`, and
 *    again inside `tv_grant_role()`. This screen sends the request and renders
 *    whatever the server answers through `apiErrorText`.
 */
export function AdminAccess() {
  const { getToken } = useAuth();
  const me = useMe();

  const [targetUserId, setTargetUserId] = useState("");
  const [role, setRole] = useState<Role | "">("");
  // Two steps on purpose. The consequence panel is the whole point of this
  // screen, and a single-click grant is a consequence panel nobody reads.
  const [confirming, setConfirming] = useState(false);

  const grant = useMutation({
    mutationFn: async (input: { targetUserId: string; role: Role }) =>
      apiFetch<GrantResult>(`/admin/users/${encodeURIComponent(input.targetUserId)}/role`, {
        token: await getToken(),
        method: "POST",
        body: { role: input.role },
      }),
    onSuccess: () => {
      setTargetUserId("");
      setRole("");
      setConfirming(false);
    },
  });

  // Any edit retracts the review. Confirming a panel that describes a different
  // role from the one now selected is the failure mode a two-step flow exists to
  // prevent, and it is trivially reachable without this.
  const revise = () => {
    setConfirming(false);
    grant.reset();
  };

  const trimmedId = targetUserId.trim();
  const isSelf = me.data !== undefined && trimmedId === me.data.userId;
  const ready = trimmedId !== "" && role !== "" && looksLikeAccountId(trimmedId);

  const onReview = (event: FormEvent) => {
    event.preventDefault();
    if (ready) setConfirming(true);
  };

  return (
    <section>
      <AdminBreadcrumb />
      <header className="page-head">
        <p className="eyebrow">Internal operations</p>
        <h2>Roles and access</h2>
        <p className="lede">
          Changing what an existing account is allowed to do on this platform. This is
          the only way to make someone an administrator: the database refuses a role
          change from anywhere else, so there is no second path and no way to do this
          quietly.
        </p>
      </header>

      <div className="notice">
        <p>
          <strong>You need the person's account id, and only they can give it to you.</strong>{" "}
          There is no staff directory here and no way to search for someone by name or
          email — the service holds no such list to ask. The person signs in, opens their
          own account page, and reads the id shown under <em>Signed in as</em>. They send
          you that. Nothing on this screen can find it for them.
        </p>
        <p>
          They must have signed in at least once. An account only exists after that, and
          a role cannot be granted to an account that does not exist yet.
        </p>
      </div>

      <form onSubmit={onReview}>
        <div className="field">
          <label htmlFor="target-user-id">Account id</label>
          <input
            id="target-user-id"
            name="target-user-id"
            value={targetUserId}
            autoComplete="off"
            spellCheck={false}
            aria-describedby="target-user-id-hint"
            onChange={(event) => {
              setTargetUserId(event.target.value);
              revise();
            }}
          />
          <p className="field-hint" id="target-user-id-hint">
            The long hyphenated id from their account page, copied whole. This screen
            cannot tell you whose account it is — see the note below the roles.
          </p>
        </div>

        <div className="field">
          <label htmlFor="target-role">Role to give this account</label>
          <select
            id="target-role"
            name="target-role"
            value={role}
            onChange={(event) => {
              setRole(event.target.value as Role | "");
              revise();
            }}
          >
            <option value="">Choose a role…</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          <p className="field-hint">
            This <em>replaces</em> whatever role the account holds now. An account holds
            one role at a time, so granting one takes the other away.
          </p>
        </div>

        {/*
          A FORMAT CHECK, NOT A RULE. `users.id` is a uuid column, so an entry
          that is not one cannot name any account that exists — this is the shape
          of the field, not a permission the browser is deciding. It is worth
          catching here because the server has no format guard on this parameter:
          a mistyped id reaches Postgres, fails the uuid cast, and comes back as a
          500, which `apiErrorText` correctly reports as a fault on our side. That
          would be a true sentence about the wrong thing, and it would send an
          operator hunting an outage that is a typo. Nothing here is refused on
          any other ground; if it is the right shape it goes to the server and the
          server decides.
        */}
        {trimmedId !== "" && !looksLikeAccountId(trimmedId) ? (
          <p className="caveat">
            That is not the shape of an account id, so nothing has been sent. An account
            id is 32 hexadecimal characters in five hyphenated groups, like{" "}
            <code>0f8b1c2d-3e4a-5b6c-7d8e-9f0a1b2c3d4e</code>. Copy it whole from their
            account page — an email address or a name will not work, and there is nothing
            here to look one up against.
          </p>
        ) : null}

        {isSelf ? (
          <p className="caveat">
            That is your own account id. Nobody changes their own role — the database
            function refuses it outright, and the reply will not explain itself, because
            this service does not give reasons for refusals. If you are trying to step
            down, another administrator has to do it for you.
          </p>
        ) : null}

        {confirming ? null : (
          <div className="actions">
            <button type="submit" disabled={!ready}>
              Review this grant
            </button>
          </div>
        )}
      </form>

      {confirming && role !== "" ? (
        <ConfirmGrant
          role={role}
          targetUserId={trimmedId}
          pending={grant.isPending}
          onCancel={revise}
          onConfirm={() => grant.mutate({ targetUserId: trimmedId, role })}
        />
      ) : null}

      {grant.error ? (
        <p role="alert">{apiErrorText(grant.error, "The role was not changed.")}</p>
      ) : null}

      {grant.data ? <GrantOutcome result={grant.data} /> : null}

      <div className="caveat">
        <p>
          <strong>What this screen cannot show you.</strong> The service answers a grant
          with the account id and its new role, and nothing else — no name, no email. So
          this page can confirm that the account you named now holds the role, and it
          cannot confirm that the account belongs to the person you meant. Check the id
          against the person, not against this screen.
        </p>
        <p>
          <strong>What is recorded, and by whom.</strong> The service writes the change to
          the audit log itself: your account as the actor, the time, the account changed,
          and the role it moved from and to. You do not have to note it anywhere, and
          nothing you could type here would be kept — this request carries the role and
          nothing more. That is unlike verifying a professional, which does require you to
          write down what you checked. If there is a reason for this grant worth keeping,
          it has to be kept outside this platform.
        </p>
      </div>
    </section>
  );
}

/** What `POST /admin/users/:id/role` answers with (accounts/routes.ts). */
type GrantResult = {
  userId: string;
  role: Role;
  /** The server's own reading of whether this role is one only an admin can give. */
  privileged: boolean;
};

const ROLE_LABELS: Record<Role, string> = {
  client: "Client",
  professional: "Professional",
  search_agent: "Search agent",
  admin: "Administrator",
};

/**
 * `users.id` is a uuid column. This is the shape of that column and not a rule
 * about who may be granted what — see the comment at the call site.
 */
function looksLikeAccountId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/**
 * THE CONSEQUENCE PANEL. The most important thing on this screen.
 *
 * Every power named below was read out of the authorization policy
 * (apps/api/src/auth/authorize.ts) rather than described from memory, and each
 * one is a real entry granted to `admin` there. "Admin access" as a phrase tells
 * an operator nothing; what it actually confers is the ability to decide which
 * attorney handles a stranger's land purchase, so it is spelled out.
 *
 * The limits are stated with the same care as the powers, because a consequence
 * panel that only escalates is one an operator learns to skim.
 */
function ConfirmGrant({
  role,
  targetUserId,
  pending,
  onCancel,
  onConfirm,
}: {
  role: Role;
  targetUserId: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="consequence">
      <h3>Before you confirm</h3>

      <p>
        You are about to give the account <code>{targetUserId}</code> the role{" "}
        <strong>{ROLE_LABELS[role]}</strong>. This replaces its current role, whatever
        that is — this screen has not been told what it is and is not guessing.
      </p>

      <RoleConsequence role={role} />

      <p>
        A role can be changed again from this screen, so this is not permanent. It is
        not undone by changing it back, though: anything done in the meantime was done,
        and stands.
      </p>

      <div className="actions">
        <button type="button" onClick={onConfirm} disabled={pending}>
          {pending ? "Sending…" : `Give this account the ${ROLE_LABELS[role]} role`}
        </button>
        <button type="button" className="quiet" onClick={onCancel} disabled={pending}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function RoleConsequence({ role }: { role: Role }) {
  if (role === "admin") {
    return (
      <>
        <p>
          <strong>This is the most powerful thing this platform can give anyone.</strong>{" "}
          An administrator can do all of the following to any case and any person on the
          platform, without asking anyone:
        </p>
        <ul>
          <li>
            <strong>Decide who is allowed to work here at all.</strong> They mark a
            professional verified, which is the single gate that makes anyone assignable
            to a case, and they revoke it, which stops work being routed to someone.
          </li>
          <li>
            <strong>Choose the attorney for a stranger's land purchase.</strong> They
            assign and reassign the professional on any case — the person who will
            examine that client's deed chain and write the opinion the client is relying
            on before paying for land.
          </li>
          <li>
            <strong>Route any case to a registry</strong>, and read and change the parcel
            details a case is routed on — the deed number, the lot, the extent, the
            district. A correction replaces the old value; there is no history of what it
            said before.
          </li>
          <li>
            <strong>Read every case on the platform</strong>, and the whole client-report
            queue: what clients have said about how their cases were handled.
          </li>
          <li>
            <strong>Manage the registry office table</strong> that all routing depends on,
            and read the audit log.
          </li>
          <li>
            <strong>Do this to other accounts, including making further
            administrators.</strong> Giving someone this role gives them the power to give
            it to anyone else, and you will not be asked about it.
          </li>
        </ul>
        <p>
          <strong>What it does not give them.</strong> Not a way to open clients' uploaded
          documents — deeds, identity papers, survey plans. Administrators hold no standing
          read on those and this grant does not create one. Not a way to change their own
          role, or yours-by-way-of-their-own. And not any authority over a legal opinion:
          the attorney owns that, and nothing on this platform lets an administrator write,
          alter or sign one.
        </p>
      </>
    );
  }

  if (role === "search_agent") {
    return (
      <>
        {/*
          NOT MINE TO FIX, AND NOT MINE TO PAPER OVER. assignSearchAgent()
          (workflow/assignment.ts) is a deliberate loud stub blocked on
          COMPLIANCE §7 Q5 — the agent split against the Intermeddling with
          Suitors Ordinance — and App.tsx already tells a search agent the same
          thing to their face. This panel reflects that position rather than
          implying the role is usable, and rather than inventing a date for it.
        */}
        <p>
          <strong>This role does not do anything yet, and the honest thing is to say so
          before you grant it.</strong> Search-agent assignment is switched off in the
          service: it is waiting on the reviewing attorney's answer about how the split
          between agent legwork and legal work sits with the Intermeddling with Suitors
          Ordinance. Until that answer comes, cases run attorney-only and no agent can be
          put on one.
        </p>
        <p>
          So the account will hold a real role with no screens behind it. When they sign
          in they are told exactly that. What the role <em>will</em> mean once it is
          enabled is narrow by design: sight of the registry extracts a search produced,
          on cases they are assigned to, and never a client's own uploads.
        </p>
        <p>
          <strong>What it costs them today is real, though.</strong> It replaces the role
          they hold now. If they are currently a client with their own cases here, moving
          them to this role takes away their access to those cases.
        </p>
      </>
    );
  }

  if (role === "professional") {
    return (
      <>
        <p>
          <strong>This grants far less than it sounds like, and it may strand them.</strong>{" "}
          It does not make anyone verified and does not make them assignable to a case. Only
          verification does that, it is a separate act, and it requires recording what
          enrolment was checked and against what source.
        </p>
        {/*
          A REAL TRAP, CHECKED AGAINST THE SERVER RATHER THAN ASSUMED. The
          professional_profiles row is created by provisionAccount() at sign-up,
          for someone who signed up AS a professional. grantRole() creates
          nothing. So an account moved into this role without that row has no
          profile, and readOwnProfile() throws NotFoundError — their own
          professional page answers 404 with no way for them or for an
          administrator to create the missing row from any screen.
        */}
        <p>
          <strong>Read this part before confirming.</strong> A professional's profile — the
          record their enrolment number and verification live in — is created when someone
          signs up as a professional. It is <em>not</em> created by this grant. If this
          account signed up as anything else, moving it here leaves it with the role and no
          profile, and their professional page will simply fail to load. There is no screen,
          here or anywhere, that can create the missing profile for them. The way that
          works is for them to be signed up as a professional in the first place.
        </p>
        <p>It also replaces their current role, and any case access that came with it.</p>
      </>
    );
  }

  return (
    <>
      <p>
        <strong>This takes privileges away rather than giving them.</strong> A client sees
        their own cases and nothing else. If this account is currently an administrator,
        they lose every administrative power the moment this goes through — including the
        power to undo it.
      </p>
      {/*
        THE NASTY ONE. Assignment checks the professional PROFILE's
        verificationStatus (workflow/assignment.ts) and never reads users.role,
        so demoting a verified professional does NOT make them unassignable. But
        the authorization policy is keyed on the principal's role, so the
        `professional` branch no longer fires for them and they lose sight of the
        case. Assignable but blind is a worse state than either, and an operator
        reaching for this as a removal tool would produce it by accident.
      */}
      <p>
        <strong>This is not how you stop work going to a professional.</strong> If this
        account has a verified professional profile, demoting it here does not touch that
        profile — they stay verified and can still be assigned to new cases, while losing
        the access they would need to do any of them. To stop routing work to someone, use
        the professional roster and revoke their verification. That is the mechanism built
        for it.
      </p>
    </>
  );
}

/**
 * The result, stating what the server said and nothing further.
 *
 * `.outcome` rather than a success banner: this is a result, not a
 * congratulation. And it names the account id rather than a person, because the
 * response carries no name and no email — see the caveat on the page.
 */
function GrantOutcome({ result }: { result: GrantResult }) {
  return (
    <div className="outcome">
      <h3>Role changed</h3>
      <p>
        The account <code>{result.userId}</code> now holds the role{" "}
        <span className="role-badge">{result.role}</span>.
        {result.privileged
          ? " The service marks this as a privileged role — one nobody can give themselves by signing up."
          : " The service does not mark this as a privileged role; it is one a person could have chosen at sign-up."}
      </p>
      <p className="muted">
        Recorded in the audit log against your account, with the role it moved from and
        to. It takes effect the next time they load a page.
      </p>
    </div>
  );
}
